<template>
    <div class="pt-3">
        <!-- Title and reset link -->
        <div class="d-flex justify-content-between align-items-center">
            <p class="text-uppercase mb-1">
                {{ $t('Fine Weight') }}
                <!--
                Uncomment if you want to show the reset link
                <a
                  class="ms-4 text-lowercase badge"
                  v-if="!isDefaultValue"
                  @click.prevent="reset"
                  href="#"
                >
                  {{ $t('catalog.reset') }}
                </a>
                -->
            </p>

            <!-- Unit dropdown -->
            <div class="dropdown mb-1">
                <button
                    class="dropdown-toggle weight-filter-toggle"
                    type="button"
                    id="unitDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    {{ currentUnit }}
                </button>
                <ul class="dropdown-menu" aria-labelledby="unitDropdown">
                    <li v-for="unit in units" :key="unit">
                        <a class="dropdown-item" href="#" @click.prevent="toggleUnit(unit)">{{ unit }}</a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="w-auto">
            <!-- Display the current min/max values -->
            <div class="slider-values d-flex justify-content-between pb-3">
                <span class="min-value">
                    {{ formatValue(currentValue[0]) }}{{ currentUnit }}
                </span>
                <span class="max-value">
                    {{ formatValue(currentValue[1]) }}{{ currentUnit }}
                </span>
            </div>

            <!-- Our custom DoubleRangeSlider (SSR-friendly), no direct DOM references needed -->
            <DoubleRangeSlider
                v-model="currentValue"
                :min="getCurrentRange()[0]"
                :max="getCurrentRange()[1]"
                @update:end="handleSliderDone"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useWeightStore } from '~/stores/WeightStore'
const DoubleRangeSlider = defineAsyncComponent(() => import('~/components/UiElements/DoubleRangeSlider.vue'));


// Props and emits
const props = defineProps({
    initRange: {
        type: [Object],
        default: () => ({
            min: 0,
            max: 100
        })
    },
    params: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['onChange'])
const route = useRoute();
const router = useRouter();

// Child components
const components = {
    DoubleRangeSlider
}

// Reactive references
const currentValue = ref([]) // [min, max]
const currentUnit = ref('oz')
const units = ref(['g', 'oz'])
const isDefaultValue = ref(true)
const rate = ref(1)
const weightStore = useWeightStore()
const ranges = ref({})

// -----------------------------------------------------
// Instead of onMounted, initialize everything right now
// -----------------------------------------------------

// 1. Parse min, max from props.params (if needed) or define defaults
let min = props.initRange['min']
let max = props.initRange['max']
// Example (if you want to parse from props.params):
// try {
//   const parsed = JSON.parse(props.params)
//   min = parsed.min ?? 1
//   max = parsed.max ?? 12500
// } catch(e) {
//   // fallback
// }

// 2. Get the rate from the store
rate.value = weightStore.rate

// 3. Prepare the boundaries for each unit
ranges.value = {
    g: [Math.floor(min), Math.ceil(max)],
    oz: [Math.floor(min / rate.value), Math.ceil(max / rate.value)]
}

// 4. Initialize currentUnit from props or default
const initUnit = computed(() => {
    if (route.query.weight_range_oz || !route.query.weight_range_g) {
        return 'oz';
    } else {
        return 'g';
    }
});

currentUnit.value = initUnit.value || currentUnit.value;
weightStore.setUnit(currentUnit.value)

const initValue = computed(() => {
    if (route.query.weight_range_oz) {
        return route.query.weight_range_oz;
    } else if (route.query.weight_range_g) {
        return route.query.weight_range_g;
    } else {
        return [];
    }
});
//
// // 5. Convert or assign currentValue from initValue.value
if (Array.isArray(initValue.value) && initValue.value.length === 2) {
    currentValue.value = initValue.value.map(v => applyRoundingRules(v))
} else if (typeof initValue.value === 'string') {
    // Convert "100,200" -> [100, 200]
    currentValue.value = initValue.value.split(',').map(item => applyRoundingRules(item))
    if (currentValue.value[1] < 0.1) {
        currentValue.value[1] = 0.1;
    }
} else {
    // If invalid or empty, set to entire range
    currentValue.value = ranges.value[currentUnit.value]
}

// ---------------------------
// Now watchers and methods...
// ---------------------------

// Watch currentValue for changes to see if it matches the default range
watch(currentValue, (newValue) => {
    const defaultRange = getCurrentRange()
    newValue = newValue.map(v => applyRoundingRules(v))
    isDefaultValue.value = JSON.stringify(newValue) === JSON.stringify(defaultRange)

    // updateValue()
})

watch(
    () => route.query,
    (newQuery) => {
        if (!newQuery.weight_range_oz && !newQuery.weight_range_g) {
            currentValue.value = getCurrentRange();
        }
    },
    { immediate: true }
);


// Helper to get the [min, max] range for the current unit
function getCurrentRange() {
    return ranges.value[currentUnit.value] || [0, 1]
}

// Called whenever we want to emit changes up
function updateValue() {
    const currentFilter = 'weight_range_' + currentUnit.value;
    const otherFilter = 'weight_range_' + (currentUnit.value === 'g' ? 'oz' : 'g');

    const newQuery = { ...route.query };
    delete newQuery[currentFilter];
    delete newQuery[otherFilter];

    if (isDefaultValue.value) {
        router.push({path: route.path, query: newQuery });
        emit('onChange', null, currentUnit.value);
        return;
    }

    const roundedValues = currentValue.value.map(v => formatValue(v));
    newQuery[currentFilter] = roundedValues.join(',');
    router.push({path: route.path, query: newQuery });

    emit('onChange', roundedValues, currentUnit.value);
}

function handleSliderDone() {
    // This fires after the user lets go of the slider
    updateValue()
    // e.g., fetch data, update store, etc.
}

// Reset slider to full range
function reset() {
    currentValue.value = getCurrentRange()
    updateValue()
}

// Switch between 'g' and 'oz'
function toggleUnit(unit) {
    if (unit === currentUnit.value) return

    currentUnit.value = unit
    // e.g. switching from g->oz or oz->g
    const factor = (currentUnit.value === 'oz') ? (1 / rate.value) : rate.value

    // Convert existing min/max
    let minVal = Math.round(currentValue.value[0] * factor)
    let maxVal = Math.round(currentValue.value[1] * factor)

    // Ensure they're within the new boundaries
    const [unitMin, unitMax] = getCurrentRange()
    if (minVal < unitMin) minVal = unitMin
    if (maxVal > unitMax) maxVal = unitMax

    // Avoid 0,0
    if (maxVal === 0) maxVal = 1

    currentValue.value = [minVal, maxVal]

    // Update the store
    weightStore.setUnit(unit)

    // If you want to immediately emit the new range:
    updateValue()
}

function formatValue(num) {
    // If num < 3, one decimal place
    if (num > 0 && num < 3) {
        return num.toFixed(1)  // e.g. 2 => "2.0", 2.56 => "2.6"
    } else {
        // If num >= 3, integer
        return Math.round(num)  // e.g. 3.2 => "3"
    }
}
function applyRoundingRules(num) {
    // parseFloat or ensure 'num' is numeric first
    const val = Number(num) || 0
    if (val < 3) {
        return parseFloat(val.toFixed(1)) // e.g. 2.34 => 2.3
    } else {
        return Math.round(val)            // e.g. 3.2 => 3
    }
}
</script>
